import { customElement } from 'lit/decorators/custom-element.js';
import { container } from 'tsyringe';
import type { Issue } from '../../domain/entities/issue.entity';
import type { IssueStatus } from '../../domain/entities/issue-status.entity';
import type { PopoverListItem } from '@o2d/popover-list';
import { get } from 'lit-translate';
import { Entity } from '../../domain/entities/entity';
import { IssueStatusRepository } from '../../domain/repositories/issue-status.repository';
import { BackendClient } from '../../connectivity/backend-client';
import { AbstractStandardTimelineView } from '../abstract-standard-timeline.view';
import type { TimelineViewConfig } from '../_shared/timeline-view.config';

const VIEW_CONFIG: TimelineViewConfig = {
    entityType: 'IssueStatus',
    sort: { strategy: 'property', path: 'sorting', direction: 'ASC' },
    yItemPath: 'status.uid',
    includes: ['status', 'project', 'employees', 'keywords', 'creator', 'employeeToIssueAssignments']
};

/**
 * Timeline view grouping issues by issue status.
 * Route: /by-issue-status
 *
 * Y-axis: IssueStatus — overrides populateTemplateFromYAxis to assign
 * the selected status to the template issue's status field.
 *
 * Context menu: Uses base class handling with isFixed + referencesCount guard.
 */
@customElement('o2d-issue-status-view')
export class IssueStatusView extends AbstractStandardTimelineView {
    protected readonly viewStateName = 'issue-status';
    protected readonly viewConfig = VIEW_CONFIG;

    /** Assigns clicked issue status to template issue. */
    protected override async populateTemplateFromYAxis(template: Issue, yAxisUid: string): Promise<void> {
        const issueStatusRepo = container.resolve(IssueStatusRepository);
        const issueStatus = await issueStatusRepo.findOneById(yAxisUid);
        if (issueStatus) {
            template.status = issueStatus;
        }
    }

    /**
     * Builds context menu with isFixed and referencesCount guards.
     * Fixed statuses and statuses with assigned issues cannot be deleted.
     */
    protected override async buildRowContextMenu(entity: Entity): Promise<PopoverListItem[]> {
        const issueStatus = entity as IssueStatus;
        const isFixed = issueStatus.isFixed;

        let deleteDisabled = isFixed;
        if (!isFixed) {
            try {
                const backendClient = container.resolve(BackendClient);
                const response = await backendClient.post<{ count: number }>('/api/v1/references', true, {
                    entity: 'IssueStatus',
                    uid: issueStatus.uid,
                    reference: 'issues'
                });
                deleteDisabled = (response?.count ?? 0) > 0;
            } catch {
                // Backend will guard — allow delete attempt on network failure
            }
        }

        return [
            { id: 'edit', label: get('ha-boards-view.row-title.edit'), type: 'item' },
            { id: 'delete', label: get('ha-boards-view.row-title.delete'), type: 'item', disabled: deleteDisabled },
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'o2d-issue-status-view': IssueStatusView;
    }
}
